import { cloneDeep, transform } from "lodash";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { resolveCssVariable } from "../../ThemeConfiguration/SiteStyles/Tokens/utils/resolveCssVariable";
import type { StyleValueMap } from "./getExistingBlockStyles";
import { isRecord, isStyleValue } from "./typePredicates";

const compareValues = (
  existingValue: unknown,
  newValue: unknown,
  cssVariables: Record<string, string>,
): boolean => {
  if (
    typeof existingValue === "string" &&
    typeof newValue === "string" &&
    (existingValue.startsWith("var(--") || newValue.startsWith("var(--"))
  ) {
    const resolvedExisting = resolveCssVariable({
      value: existingValue,
      cssVariables,
    });
    const resolvedNew = resolveCssVariable({
      value: newValue,
      cssVariables,
    });
    return resolvedExisting === resolvedNew;
  }
  return existingValue === newValue;
};

const mergeNestedStyles = (
  styles: StyleValueMap | StyleValue,
  formValue: unknown,
  cssVariables: Record<string, string>,
): StyleValueMap | StyleValue => {
  if (!isRecord(formValue)) {
    return styles;
  }

  if (isStyleValue(styles)) {
    if (compareValues(styles.value, formValue, cssVariables)) {
      return styles;
    }
    return { ...styles, value: formValue };
  }

  return transform(
    formValue,
    (acc: StyleValueMap, value: unknown, key: string) => {
      const existingValue = styles[key];

      if (isStyleValue(existingValue)) {
        if (compareValues(existingValue.value, value, cssVariables)) {
          acc[key] = existingValue;
        } else {
          acc[key] = { ...existingValue, value };
        }
      } else if (isRecord(value) && isRecord(existingValue)) {
        acc[key] = mergeNestedStyles(existingValue, value, cssVariables);
      } else {
        acc[key] = existingValue;
      }
    },
    cloneDeep(styles),
  );
};

export const mergeNewStyles = (
  styles: StyleValueMap | StyleValue,
  formValue: unknown,
  cssVariables: Record<string, string>,
): StyleValueMap | StyleValue =>
  mergeNestedStyles(styles, formValue, cssVariables);
